import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { genericTokenIcon } from '../config';
import { useNavigate } from 'react-router-dom';
import { useWatchlist } from '../watchlist/WatchlistContext';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { formatPrice as formatNodeListPrice } from '../NodeLists';

const useStyles = makeStyles((theme) => ({
  hero: {
    padding: '16px 18px 14px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.28), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.12)',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: { padding: '10px 12px' },
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    flexWrap: 'wrap',
  },
  identity: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    minWidth: 0,
    flex: 1,
    textAlign: 'left',
  },
  icon: {
    width: 56,
    height: 56,
    borderRadius: 14,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(5, 8, 20, 0.72)',
    objectFit: 'cover',
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      width: 46,
      height: 46,
      borderRadius: 12,
    },
  },
  title: {
    fontSize: '1.7rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.1,
    color: '#e5e7ff',
    textShadow: '0 0 14px rgba(77,121,255,0.45)',
    wordBreak: 'break-word',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
    },
  },
  subtitle: {
    fontSize: 12,
    fontFamily: 'monospace',
    color: '#9ea4d8',
    marginTop: 4,
    wordBreak: 'break-all',
  },
  subtitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  copyButton: {
    color: '#cfd6ff',
    padding: 3,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.06)',
    },
  },
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 10,
  },
  badge: {
    borderRadius: 999,
    border: '1px solid rgba(77, 121, 255, 0.28)',
    background: 'rgba(77, 121, 255, 0.12)',
    color: '#cfd6ff',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
  },
  trustBadge: {
    borderRadius: 999,
    border: '1px solid rgba(158, 247, 197, 0.28)',
    background: 'rgba(158, 247, 197, 0.12)',
    color: '#9ef7c5',
    fontSize: 11,
    fontWeight: 800,
    padding: '4px 10px',
  },
  honeypotBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 14,
    padding: '12px 16px',
    borderRadius: 12,
    border: '1.5px solid rgba(255, 60, 60, 0.7)',
    background: 'linear-gradient(90deg, rgba(180,20,20,0.22) 0%, rgba(255,60,60,0.10) 100%)',
    boxShadow: '0 0 18px rgba(255,60,60,0.18)',
  },
  honeypotIcon: {
    fontSize: 32,
    lineHeight: 1,
    flexShrink: 0,
  },
  honeypotTitle: {
    color: '#ff4c4c',
    fontWeight: 900,
    fontSize: 14,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    lineHeight: 1.2,
  },
  honeypotDesc: {
    color: '#ffaaaa',
    fontSize: 12,
    marginTop: 2,
    lineHeight: 1.5,
  },
  securityBadgeDanger: {
    borderRadius: 999,
    border: '1px solid rgba(255, 60, 60, 0.45)',
    background: 'rgba(255, 60, 60, 0.12)',
    color: '#ff7070',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
  },
  securityBadgeWarn: {
    borderRadius: 999,
    border: '1px solid rgba(255, 190, 50, 0.45)',
    background: 'rgba(255, 190, 50, 0.10)',
    color: '#ffd166',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  actionButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '6px 14px',
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: '#3e61cc' },
  },
  secondaryButton: {
    color: '#d9defa',
    borderColor: 'rgba(255,255,255,0.18)',
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 10,
    padding: '6px 14px',
    whiteSpace: 'nowrap',
  },
  statusCopy: {
    color: '#c8cdf7',
    fontSize: 13,
    marginTop: 12,
    lineHeight: 1.5,
  },
  claimSummary: {
    marginTop: 12,
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.05)',
    maxWidth: 560,
  },
  claimLinkRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  claimLink: {
    color: '#9ef7c5',
    fontSize: 12,
    textDecoration: 'none',
  },
}));

function formatUsd(value) {
  const formatted = formatNodeListPrice(value);
  return formatted || '—';
}

function normalizeWatchChain(chain) {
  const raw = String(chain || '').trim().toUpperCase();
  if (!raw) return 'ETH';
  if (raw === 'ETHEREUM') return 'ETH';
  if (raw === 'BINANCE' || raw === 'BINANCE SMART CHAIN') return 'BSC';
  return raw;
}

function getFirstValue(source, keys) {
  if (!source) return null;
  for (const key of keys) {
    const value = source[key];
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return null;
}

function formatFlags(value) {
  if (!value) return '';
  if (Array.isArray(value)) {
    return value.filter(Boolean).map((item) => String(item)).join(', ');
  }
  return String(value);
}

function buildTokenContext(address, metadata) {
  const tokenAddress = String(address || '').trim();
  const tokenLabel = metadata && (metadata.symbol || metadata.name)
    ? `${metadata.symbol || metadata.name}${metadata && metadata.name && metadata.symbol ? ` • ${metadata.name}` : ''}`
    : tokenAddress;
  const params = new URLSearchParams();
  if (tokenAddress) params.set('tokenAddress', tokenAddress);
  return {
    search: params.toString() ? `?${params.toString()}` : '',
    state: {
      tokenAddress,
      tokenLabel,
      tokenSymbol: metadata && metadata.symbol ? metadata.symbol : '',
      from: `/token/${tokenAddress}`,
    },
  };
 }

// Derive security display info from the metadata.security object
function useSecurityDisplay(security) {
  if (!security || !security.checked) return { isHoneypot: false, criticalFlags: [], warnFlags: [], taxLine: null };

  const isHoneypot    = !!(security.is_honeypot || security.cannot_sell_all || security.fake_token);
  const criticalFlags = Array.isArray(security.critical_flags) ? security.critical_flags : [];
  const warnFlags     = Array.isArray(security.warn_flags)     ? security.warn_flags     : [];

  let taxLine = null;
  if (security.sell_tax != null && security.sell_tax > 0) {
    taxLine = `Sell tax ${security.sell_tax}%`;
    if (security.buy_tax != null && security.buy_tax > 0) taxLine += ` · Buy tax ${security.buy_tax}%`;
  } else if (security.buy_tax != null && security.buy_tax > 0) {
    taxLine = `Buy tax ${security.buy_tax}%`;
  }

  return { isHoneypot, criticalFlags, warnFlags, taxLine };
}

export default function TokenHeader({ address, metadata, liquidity, loading, error }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const { isWatched, toggleWatch, setAlert, items } = useWatchlist();
  const [actionError, setActionError] = useState('');
  const [alertAbove, setAlertAbove] = useState('');
  const [alertBelow, setAlertBelow] = useState('');
  const [alertPctChange, setAlertPctChange] = useState('');
  const [alertPctDirection, setAlertPctDirection] = useState('either');
  const [alertPctWindowHours, setAlertPctWindowHours] = useState('24');
  const [alertSaved, setAlertSaved] = useState(false);
  const [copyNotice, setCopyNotice] = useState('');
  const liquidityLatest = liquidity && liquidity.latest ? liquidity.latest : null;
  const shortAddress = address ? `${address.slice(0, 6)}...${address.slice(-4)}` : 'Unknown';
  const title = metadata && (metadata.name || metadata.symbol)
    ? `${metadata.name || metadata.symbol}${metadata.symbol ? ` (${metadata.symbol})` : ''}`
    : 'Token Intelligence Terminal';
  const iconSrc = metadata && metadata.icon ? metadata.icon : genericTokenIcon;
  const categories = metadata && Array.isArray(metadata.categories) ? metadata.categories.slice(0, 3) : [];
  const resolvedChain = normalizeWatchChain(metadata && metadata.chain);
  const fdvUsd = getFirstValue(metadata, ['fdv_usd', 'fdvUSD', 'fdv']) || getFirstValue(liquidityLatest, ['fdv_usd', 'fdvUSD', 'fdv']);
  const priceConfidence = getFirstValue(metadata, ['price_confidence', 'priceConfidence']) || getFirstValue(liquidityLatest, ['price_confidence', 'priceConfidence']);
  const fragilityScore = getFirstValue(metadata, ['fragility_score', 'fragilityScore']) || getFirstValue(liquidityLatest, ['fragility_score', 'fragilityScore']);
  const anomalyFlags = getFirstValue(metadata, ['anomaly_flags', 'anomalyFlags']) || getFirstValue(liquidityLatest, ['anomaly_flags', 'anomalyFlags']);
  const sourceDex = getFirstValue(metadata, ['price_source_dex', 'priceSourceDex']) || getFirstValue(liquidityLatest, ['price_source_dex', 'priceSourceDex']);
  const sourceType = getFirstValue(metadata, ['price_source_type', 'priceSourceType']) || getFirstValue(liquidityLatest, ['price_source_type', 'priceSourceType']);
  const { isHoneypot, criticalFlags, warnFlags, taxLine } = useSecurityDisplay(metadata && metadata.security);
  const claim = metadata && metadata.claim ? metadata.claim : null;
  const claimBadgeLabel = claim && claim.claimed
    ? ((claim.proofMethod === 'deployer' || claim.proofMethod === 'owner_call') ? 'Owner-verified' : 'Claimed')
    : claim && claim.stale
      ? 'Claim Stale'
    : 'Unclaimed';
  const claimHasStoredKey = !!(() => {
    try {
      return address && typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem(`e3d_claim_key:${String(address).toLowerCase()}`);
    } catch (e) {
      return false;
    }
  })();
  const watched = !!(address && isWatched(normalizeWatchChain(resolvedChain), address));
  const statusText = actionError
    ? actionError
    : copyNotice
      ? copyNotice
    : loading
      ? 'Loading token metadata…'
      : error
        ? error
        : `Live metadata connected${metadata && metadata.price_updated_at ? ` • Price updated ${metadata.price_updated_at}` : ''}`;

  useEffect(() => {
    if (!copyNotice) return undefined;
    const t = setTimeout(() => setCopyNotice(''), 1800);
    return () => clearTimeout(t);
  }, [copyNotice]);

  // Sync alert thresholds from watched item when address changes or items update
  useEffect(() => {
    if (!address || !items) return;
    const addrLc = address.toLowerCase();
    const item = items.find((it) => it.address && it.address.toLowerCase() === addrLc);
    if (item) {
      setAlertAbove(item.alertAbove != null ? String(item.alertAbove) : '');
      setAlertBelow(item.alertBelow != null ? String(item.alertBelow) : '');
      setAlertPctChange(item.alertPctChange != null ? String(item.alertPctChange) : '');
      setAlertPctDirection(item.alertPctDirection || 'either');
      setAlertPctWindowHours(item.alertPctWindowHours != null ? String(item.alertPctWindowHours) : '24');
    }
  }, [address, items]);

  const handleSaveAlert = async () => {
    if (!address || !watched) return;
    setActionError('');
    try {
      await setAlert({ chain: resolvedChain, address, alertAbove: alertAbove || null, alertBelow: alertBelow || null, alertPctChange: alertPctChange || null, alertPctDirection, alertPctWindowHours });
      setAlertSaved(true);
      setTimeout(() => setAlertSaved(false), 2000);
    } catch (e) {
      setActionError('Failed to save alert.');
    }
  };

  const copyText = async (text) => {
    const t = String(text || '').trim();
    if (!t) return false;
    try {
      if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(t);
        return true;
      }
    } catch (e) {
    }

    try {
      const ta = document.createElement('textarea');
      ta.value = t;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleCopyAddress = async () => {
    if (!address) return;
    setActionError('');
    const ok = await copyText(address);
    setCopyNotice(ok ? 'Address copied' : 'Copy failed');
  };

  const openGraph = () => {
    if (!address) return;
    navigate(`/links/${address}`);
  };

  const openStories = () => {
    const tokenContext = buildTokenContext(address, metadata);
    navigate(`/stories${tokenContext.search}`, { state: tokenContext.state });
  };

  const openTheses = () => {
    const tokenContext = buildTokenContext(address, metadata);
    navigate(`/thesis${tokenContext.search}`, { state: tokenContext.state });
  };

  const handleToggleWatch = async () => {
    if (!address) return;
    setActionError('');
    try {
      await toggleWatch({
        chain: resolvedChain,
        address,
        label: metadata && (metadata.name || metadata.symbol) ? `${metadata.name || metadata.symbol}` : shortAddress,
        type: 'token',
      });
    } catch (e) {
      setActionError('Failed to update watchlist state.');
    }
  };

  const openClaimDialog = (mode) => {
    if (!address) return;
    navigate(`/token/${address}/claim?mode=${mode}`);
  };

  if (loading && !metadata) {
    return (
      <Box className={classes.hero}>
        <Box className={classes.titleRow}>
          <Box className={classes.identity}>
            <Skeleton variant="rect" width={56} height={56} style={{ borderRadius: 14, flexShrink: 0 }} />
            <Box style={{ flex: 1 }}>
              <Skeleton variant="text" width={220} height={36} />
              <Skeleton variant="text" width={340} height={18} style={{ marginTop: 4 }} />
              <Box className={classes.metaRow}>
                {[80, 90, 100, 110, 95, 105].map((w, i) => (
                  <Skeleton key={i} variant="rect" width={w} height={24} style={{ borderRadius: 999 }} />
                ))}
              </Box>
            </Box>
          </Box>
          <Box className={classes.actionRow}>
            {[120, 120, 110, 100].map((w, i) => (
              <Skeleton key={i} variant="rect" width={w} height={34} style={{ borderRadius: 10 }} />
            ))}
          </Box>
        </Box>
        <Skeleton variant="text" width={260} height={18} style={{ marginTop: 12 }} />
      </Box>
    );
  }

  return (
    <Box className={classes.hero}>
      <Box className={classes.titleRow}>
        <Box className={classes.identity}>
          <Box component="img" src={iconSrc} alt="Token" className={classes.icon} />
          <Box>
            <Typography className={classes.title}>{title}</Typography>
            <Box className={classes.subtitleRow}>
              <Typography className={classes.subtitle}>{address || 'No token address provided'}</Typography>
              {address ? (
                <Tooltip title="Copy address" placement="top">
                  <IconButton
                    size="small"
                    className={classes.copyButton}
                    onClick={handleCopyAddress}
                    aria-label="Copy token address"
                  >
                    <FileCopyIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
            <Box className={classes.metaRow}>
              <Box className={classes.badge}>Token: {shortAddress}</Box>
              <Box className={classes.badge}>Chain: {resolvedChain}</Box>
              <Box className={classes.badge}>Price: {formatUsd(metadata && metadata.price_usd)}</Box>
              <Box className={classes.badge}>Market Cap: {formatUsd(metadata && metadata.market_cap_usd)}</Box>
              <Box className={classes.badge}>FDV: {formatUsd(fdvUsd)}</Box>
              <Tooltip title={sourceDex || sourceType ? `${sourceDex || '-'}${sourceType ? ` (${sourceType})` : ''}` : 'No pricing source'}>
                <Box className={classes.badge}>Liquidity: {formatUsd(metadata && metadata.liquidity_usd)}</Box>
              </Tooltip>
              {priceConfidence ? <Box className={classes.trustBadge}>Confidence: {String(priceConfidence)}</Box> : null}
              {fragilityScore !== null && fragilityScore !== undefined && fragilityScore !== '' ? <Box className={classes.securityBadgeWarn}>Fragility: {fragilityScore}</Box> : null}
              {formatFlags(anomalyFlags) ? <Box className={classes.securityBadgeDanger}>Anomalies: {formatFlags(anomalyFlags)}</Box> : null}
              {categories.length ? <Box className={classes.badge}>{categories.join(', ')}</Box> : null}
              {/* Security risk badges */}
              {criticalFlags.map(f => (
                <Box key={f} className={classes.securityBadgeDanger}>⛔ {f.replace(/_/g, ' ')}</Box>
              ))}
              {warnFlags.map(f => (
                <Box key={f} className={classes.securityBadgeWarn}>⚠️ {f.replace(/_/g, ' ')}</Box>
              ))}
              {taxLine && <Box className={classes.securityBadgeDanger}>{taxLine}</Box>}
              <Box className={claim && claim.claimed ? classes.trustBadge : classes.badge}>{claimBadgeLabel}</Box>
              <Box className={classes.trustBadge}>Metadata Live</Box>
            </Box>
            {claim && (claim.claimed || claim.stale) ? (
              <Box className={classes.claimSummary}>
                <Typography variant="body2" style={{ color: '#e5e7ff' }}>
                  {claim.stale
                    ? 'A prior owner() claim no longer matches the current on-chain owner.'
                    : claim.proofMethod === 'deployer' || claim.proofMethod === 'owner_call'
                    ? 'Owner-authored listing verified on stronger proof.'
                    : 'Signature-authored listing provided by the claimant wallet.'}
                </Typography>
                {claim.claimantWallet ? (
                  <Typography variant="body2" style={{ color: '#9ea4d8', marginTop: 4 }}>
                    Claimant: {claim.claimantWallet}
                  </Typography>
                ) : null}
                {claim.claimed && (claim.website || claim.contact || (claim.socials && Object.keys(claim.socials).length)) ? (
                  <Box className={classes.claimLinkRow}>
                    {claim.website ? <a className={classes.claimLink} href={claim.website} target="_blank" rel="noreferrer">Website</a> : null}
                    {claim.contact ? <Typography variant="body2" style={{ color: '#cfd6ff' }}>{claim.contact}</Typography> : null}
                    {claim.socials && Object.entries(claim.socials).map(([key, value]) => (
                      <a key={key} className={classes.claimLink} href={String(value)} target="_blank" rel="noreferrer">{key}</a>
                    ))}
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          <Box className={classes.actionRow}>
            <Button className={classes.actionButton} variant="contained" onClick={handleToggleWatch} disabled={!address}>
              {watched ? 'Unwatch Token' : 'Watch Token'}
            </Button>
            {!claim || !claim.claimed ? (
              <Button className={classes.secondaryButton} variant="outlined" onClick={() => openClaimDialog('claim')} disabled={!address}>
                Claim This Token
              </Button>
            ) : null}
            <Button className={classes.secondaryButton} variant="outlined" onClick={() => openClaimDialog('manage')}>
              My Claimed Tokens
            </Button>
            {claim && claim.claimed && claimHasStoredKey ? (
              <Button className={classes.secondaryButton} variant="outlined" onClick={() => openClaimDialog('edit')} disabled={!address}>
                Edit Listing
              </Button>
            ) : null}
            <Button className={classes.secondaryButton} variant="outlined" onClick={openGraph} disabled={!address}>Open Full Graph</Button>
            <Button className={classes.secondaryButton} variant="outlined" onClick={openStories}>Open Stories</Button>
            <Button className={classes.secondaryButton} variant="outlined" onClick={openTheses}>Open Theses</Button>
          </Box>
          {watched && (() => {
            const inputSt = { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, color: '#fff', fontSize: 11, padding: '3px 7px', outline: 'none' };
            const selSt = { ...inputSt, cursor: 'pointer' };
            const lblSt = { color: '#9ea4d8', fontSize: 11, whiteSpace: 'nowrap' };
            return (
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <span style={lblSt}>Price level:</span>
                  <input type="number" placeholder="above $" value={alertAbove} onChange={(e) => setAlertAbove(e.target.value)}
                    style={{ ...inputSt, width: 80 }} />
                  <input type="number" placeholder="below $" value={alertBelow} onChange={(e) => setAlertBelow(e.target.value)}
                    style={{ ...inputSt, width: 80 }} />
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <span style={lblSt}>% move:</span>
                  <input type="number" placeholder="e.g. 10" value={alertPctChange} onChange={(e) => setAlertPctChange(e.target.value)}
                    style={{ ...inputSt, width: 64 }} />
                  <span style={{ ...lblSt, color: '#666' }}>%</span>
                  <select value={alertPctDirection} onChange={(e) => setAlertPctDirection(e.target.value)} style={{ ...selSt, width: 70 }}>
                    <option value="either">Either</option>
                    <option value="up">Up</option>
                    <option value="down">Down</option>
                  </select>
                  <select value={alertPctWindowHours} onChange={(e) => setAlertPctWindowHours(e.target.value)} style={{ ...selSt, width: 56 }}>
                    <option value="4">4h</option>
                    <option value="12">12h</option>
                    <option value="24">24h</option>
                    <option value="48">48h</option>
                  </select>
                  <Button onClick={handleSaveAlert} variant="outlined"
                    style={{ textTransform: 'none', fontSize: 11, padding: '2px 10px', borderRadius: 6, color: alertSaved ? '#9ef7c5' : '#cfd6ff', borderColor: alertSaved ? 'rgba(158,247,197,0.5)' : 'rgba(255,255,255,0.2)', whiteSpace: 'nowrap' }}>
                    {alertSaved ? 'Saved ✓' : 'Set Alerts'}
                  </Button>
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Box>
      {/* ── Honeypot warning banner ── */}
      {isHoneypot && (
        <Box className={classes.honeypotBanner} role="alert" aria-live="assertive">
          <Box className={classes.honeypotIcon} aria-hidden="true">🍯</Box>
          <Box>
            <Typography className={classes.honeypotTitle}>
              ⛔ Honeypot Confirmed — Do Not Trade
            </Typography>
            <Typography className={classes.honeypotDesc}>
              On-chain security analysis (GoPlus) has confirmed this token's sell function is blocked or
              heavily restricted. You can buy, but you likely cannot sell. This is a known scam pattern.
              {taxLine ? ` ${taxLine}.` : ''}
              {criticalFlags.length ? ` Flags: ${criticalFlags.join(', ')}.` : ''}
            </Typography>
          </Box>
        </Box>
      )}

      <Typography className={classes.statusCopy}>{statusText}</Typography>
    </Box>
  );
}
